// FollowupPendingModal — manual follow-up + final-notice send for a
// pending booking. Mirrors the legacy pending_bookings.php "Follow-up"
// modal with two tabs (Regular Follow-up | Final Notice), each offering
// Email / SMS / Both. Final Notice tab requires a confirm prompt and
// triggers the 24-hour deadline countdown.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, MessageSquare, Send, AlertTriangle, PhoneOff } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { useToast } from '../../../hooks/useToast';
import {
  useSendFinalNotice,
  useSendFollowup,
  useSendIncorrectContact,
} from '../hooks/usePending';
import type {
  IncorrectContactPhoneChoice,
  IncorrectContactReason,
  PendingFollowupChannel,
} from '../../../types/api';
import styles from './FollowupPendingModal.module.css';

export type FollowupTab = 'regular' | 'final_notice' | 'incorrect';

export interface FollowupPendingModalProps {
  open: boolean;
  encId: string | null;
  /** Reference shown in the modal header. Optional. */
  reference?: string | null;
  /** Initial tab — defaults to 'regular'. The list page passes
   *  'final_notice' when the user clicks the Final Notice action. */
  initialTab?: FollowupTab;
  onClose: () => void;
  /** Optional callback fired after a successful send (page can re-route or
   *  toast extra context). */
  onSent?: () => void;
}

const FINAL_NOTICE_CONFIRM =
  'This will send a final notice and start the 24-hour cancellation countdown. Continue?';

export function FollowupPendingModal({
  open,
  encId,
  reference,
  initialTab = 'regular',
  onClose,
  onSent,
}: FollowupPendingModalProps): JSX.Element | null {
  const toast = useToast();
  const [tab, setTab] = useState<FollowupTab>(initialTab);

  const followup = useSendFollowup(encId ?? undefined);
  const finalNotice = useSendFinalNotice(encId ?? undefined);
  const incorrect = useSendIncorrectContact(encId ?? undefined);
  const [phoneChoice, setPhoneChoice] = useState<IncorrectContactPhoneChoice>('primary');
  const submitting = followup.isPending || finalNotice.isPending || incorrect.isPending;

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  if (!open) return null;

  function handleError(err: unknown, fallback: string) {
    const msg = axios.isAxiosError(err)
      ? (err.response?.data as { message?: string } | undefined)?.message ?? fallback
      : fallback;
    toast.error(msg);
  }

  async function sendIncorrect(reason: IncorrectContactReason) {
    if (!encId) return;
    try {
      const result = await incorrect.mutateAsync({
        reason,
        ...(reason === 'email_incorrect' ? { phone_choice: phoneChoice } : {}),
      });
      toast.success(result.message);
      onSent?.();
      onClose();
    } catch (err) {
      handleError(err, 'Could not send the notice.');
    }
  }

  async function send(type: PendingFollowupChannel) {
    if (!encId) return;
    if (tab === 'final_notice') {
      const ok = window.confirm(FINAL_NOTICE_CONFIRM);
      if (!ok) return;
    }
    try {
      if (tab === 'regular') {
        await followup.mutateAsync({ type });
        toast.success(`Follow-up sent via ${labelFor(type)}.`);
      } else {
        const result = await finalNotice.mutateAsync({ type });
        toast.success(
          `Final notice #${result.notice_number} sent via ${labelFor(type)}.`,
        );
      }
      onSent?.();
      onClose();
    } catch (err) {
      handleError(
        err,
        tab === 'regular'
          ? 'Could not send follow-up.'
          : 'Could not send final notice.',
      );
    }
  }

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title={
        <span className={styles.titleRow}>
          <Send size={16} aria-hidden />
          Send Follow-up Communication
        </span>
      }
      size="md"
      preventClose={submitting}
      footer={
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
      }
    >
      {reference ? (
        <p className={styles.subtitle}>Pending {reference}</p>
      ) : null}

      <div className={styles.tabs} role="tablist" aria-label="Follow-up type">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'regular'}
          className={tab === 'regular' ? styles.tabActive : styles.tab}
          onClick={() => setTab('regular')}
        >
          <Mail size={14} aria-hidden /> Regular Follow-up
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'final_notice'}
          className={tab === 'final_notice' ? styles.tabActiveDanger : styles.tab}
          onClick={() => setTab('final_notice')}
        >
          <AlertTriangle size={14} aria-hidden /> Final Notice
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'incorrect'}
          className={tab === 'incorrect' ? styles.tabActive : styles.tab}
          onClick={() => setTab('incorrect')}
        >
          <PhoneOff size={14} aria-hidden /> Incorrect Details
        </button>
      </div>

      {tab === 'incorrect' ? (
        <>
          <div className={styles.infoBox} role="note">
            <strong>Incorrect contact details.</strong>
            <span>
              Tell the customer the phone number or email address we hold for them is
              wrong, so they can send an updated one. This does not change the
              booking status. A note is added to the record.
            </span>
          </div>
          <div className={styles.options}>
            <SendOption
              label="Phone number is incorrect"
              subLabel="Notify the customer by email"
              icon={<Mail size={20} aria-hidden />}
              disabled={submitting}
              onClick={() => void sendIncorrect('phone_incorrect')}
            />
            <div className={styles.infoBox}>
              <label htmlFor="incorrect-phone-choice">
                <strong>Send the SMS to</strong>
              </label>
              <select
                id="incorrect-phone-choice"
                aria-label="SMS recipient"
                value={phoneChoice}
                onChange={(e) => setPhoneChoice(e.target.value as IncorrectContactPhoneChoice)}
                disabled={submitting}
              >
                <option value="primary">Primary number</option>
                <option value="secondary">Secondary number</option>
                <option value="both">Both numbers</option>
              </select>
            </div>
            <SendOption
              label="Email address is incorrect"
              subLabel="Notify the customer by SMS"
              icon={<MessageSquare size={20} aria-hidden />}
              disabled={submitting}
              onClick={() => void sendIncorrect('email_incorrect')}
            />
          </div>
        </>
      ) : null}

      {tab === 'incorrect' ? null : tab === 'regular' ? (
        <div className={styles.infoBox} role="note">
          <strong>Regular Follow-up.</strong>
          <span>
            Send a gentle reminder to the customer. This will not change the
            booking status.
          </span>
        </div>
      ) : (
        <div className={styles.dangerBox} role="alert">
          <strong>About Final Notice.</strong>
          <span>
            Sending a final notice will update the booking status to
            "Final Notice" and give the customer 24 hours to respond. You can
            send multiple final notices if needed.
          </span>
        </div>
      )}

      {tab === 'incorrect' ? null : (
      <div className={styles.options}>
        <SendOption
          label="Send Email Only"
          subLabel={
            tab === 'regular'
              ? 'Send follow-up email to customer'
              : '24-hour warning. Updates status to Final Notice'
          }
          icon={<Mail size={20} aria-hidden />}
          danger={tab === 'final_notice'}
          disabled={submitting}
          onClick={() => void send('email')}
        />
        <SendOption
          label="Send SMS Only"
          subLabel={
            tab === 'regular'
              ? 'Send follow-up SMS to customer'
              : '24-hour warning. Updates status to Final Notice'
          }
          icon={<MessageSquare size={20} aria-hidden />}
          danger={tab === 'final_notice'}
          disabled={submitting}
          onClick={() => void send('sms')}
        />
        <SendOption
          label="Send Both Email & SMS"
          subLabel={
            tab === 'regular'
              ? 'Send follow-up via email and SMS'
              : '24-hour warning. Updates status to Final Notice'
          }
          icon={<Send size={20} aria-hidden />}
          danger={tab === 'final_notice'}
          disabled={submitting}
          onClick={() => void send('both')}
        />
      </div>
      )}
    </Modal>
  );
}

function labelFor(type: PendingFollowupChannel): string {
  if (type === 'both') return 'email and SMS';
  return type;
}

interface SendOptionProps {
  label: string;
  subLabel: string;
  icon: JSX.Element;
  danger?: boolean;
  disabled: boolean;
  onClick: () => void;
}

function SendOption({ label, subLabel, icon, danger, disabled, onClick }: SendOptionProps) {
  return (
    <button
      type="button"
      className={danger ? styles.optionDanger : styles.option}
      onClick={onClick}
      disabled={disabled}
    >
      <span className={danger ? styles.optionIconDanger : styles.optionIcon}>
        {icon}
      </span>
      <span className={styles.optionContent}>
        <span className={styles.optionLabel}>{label}</span>
        <span className={styles.optionSub}>{subLabel}</span>
      </span>
    </button>
  );
}
